import styled from '@emotion/styled'
import { Typography } from '@mui/material'
import { FC, useMemo, useState } from 'react'
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil'
import * as atoms from 'src/atoms/user-lists/user-lists-modal'
import type { MatchedData } from 'src/atoms/user-lists/user-lists-modal'
import { userListsClient } from 'src/clients'
import { UserListItem } from 'src/common-services'
import { Alert } from 'src/components/alert/alert'
import { Button } from 'src/components/button'
import { Spinner } from 'src/components/spinner'
import { GridTable, GridTableHeaderItem, GridTableItem, GridTableRow } from 'src/components/table'
import { Colors } from 'src/components/theme'
import { useSetUpUserLists } from 'src/hooks/use-setup-userlists'

type FilterOption = 'all' | 'matched' | 'unmatched' | 'duplicated'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 1120px;
`

const FilterWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
`

const FilterOptions = styled.div`
  display: flex;
  flex-direction: row;
  border: 1px solid ${Colors.neutral200};
  gap: 10px;
  border-radius: 4px;
`

const FilterOption = styled.div<{ active?: boolean }>`
  display: flex;
  flex-direction: row;
  padding: 4px 16px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: ${(props) => (props.active ? Colors.blueDark : Colors.textLight)};
  background-color: ${(props) => (props.active ? Colors.primaryBlue50 : 'transparent')};
  margin: 2px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 700;
  user-select: none;
`

const Circle = styled.div<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${(props) => props.color};
`

const Footer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`

const ConfirmButton = styled(Button)`
  background-color: ${Colors.blueDark};
`

const CancelButton = styled(Button)`
  border: 1px solid ${Colors.blueDark};
  color: ${Colors.blueDark};
`

const Headers: FC = () => (
  <>
    <GridTableHeaderItem xs={3}>Address</GridTableHeaderItem>
    <GridTableHeaderItem xs={3}>Matched Name</GridTableHeaderItem>
    <GridTableHeaderItem xs={6}>Matched Address</GridTableHeaderItem>
  </>
)

type RowProps = {
  item: MatchedData
  odd: boolean
}

const Row: FC<RowProps> = ({ item, odd }) => {
  return (
    <GridTableRow odd={odd} striped>
      <GridTableItem xs={3} dashed>
        {item.address}
      </GridTableItem>
      {item.googlePlace ? (
        <>
          <GridTableItem xs={3}>{item.googlePlace.name}</GridTableItem>
          <GridTableItem xs={6}>{item.googlePlace.formatted_address}</GridTableItem>
        </>
      ) : (
        <GridTableItem xs={9}>
          <Typography color={Colors.blueDark}>Unmatched</Typography>
        </GridTableItem>
      )}
    </GridTableRow>
  )
}

type FiltersProps = {
  filter: FilterOption
  onChageFilter: (filter: FilterOption) => void
}
const Filters: FC<FiltersProps> = ({ filter, onChageFilter }) => {
  const data = useRecoilValue(atoms.matchedData)
  const matchedQ = useRecoilValue(atoms.matchedDataQuantity)
  const unmatchedQ = useRecoilValue(atoms.unmatchedDataQuantity)
  const duplicatedQ = useRecoilValue(atoms.duplicatedMatchedDataQuantity)

  return (
    <FilterWrapper>
      <Typography fontWeight="700">Filter Results</Typography>
      <FilterOptions>
        <FilterOption
          active={filter === 'all'}
          onClick={() => onChageFilter('all')}>{`All ${data.length} Rows`}</FilterOption>
        <FilterOption active={filter === 'matched'} onClick={() => onChageFilter('matched')}>
          <Circle color={Colors.primaryGreen600} />
          Matched ({matchedQ})
        </FilterOption>
        <FilterOption active={filter === 'duplicated'} onClick={() => onChageFilter('duplicated')}>
          <Circle color={Colors.yellowDark} />
          Duplicated ({duplicatedQ})
        </FilterOption>
        <FilterOption active={filter === 'unmatched'} onClick={() => onChageFilter('unmatched')}>
          <Circle color={Colors.primaryRed600} />
          Unmatched ({unmatchedQ})
        </FilterOption>
      </FilterOptions>
    </FilterWrapper>
  )
}

export const MatchDataResultsContent: FC = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()
  const [filter, setFilter] = useState<FilterOption>('all')

  const matchedData = useRecoilValue(atoms.matchedData)
  const uniqueMatchData = useRecoilValue(atoms.uniqueMatchedData)
  const duplicatedMatchData = useRecoilValue(atoms.duplicatedMatchedData)
  const unmatchedData = useRecoilValue(atoms.unmatchedData)
  const matchedQ = useRecoilValue(atoms.matchedDataQuantity)
  const selectedListId = useRecoilValue(atoms.selectedListId)
  const setUploadSection = useSetRecoilState(atoms.uploadSection)
  const resetMatchedData = useResetRecoilState(atoms.matchedData)
  const resetCsvFile = useResetRecoilState(atoms.csvFile)
  const setSuccesssMessage = useSetRecoilState(atoms.saveItemSuccessMessage)
  const { setUpUserLists } = useSetUpUserLists()

  const rows = useMemo(() => {
    let list: MatchedData[] = []
    if (filter === 'all') {
      list = matchedData
    } else if (filter === 'matched') {
      list = uniqueMatchData
    } else if (filter === 'duplicated') {
      list = duplicatedMatchData
    } else if (filter === 'unmatched') {
      list = unmatchedData
    }

    return list.map((item, index) => <Row key={item.address} item={item} odd={index % 2 === 0} />)
  }, [matchedData, filter, uniqueMatchData, duplicatedMatchData, unmatchedData])

  const onCancel = () => {
    resetMatchedData()
    setUploadSection('match-data')
  }

  const onConfirm = async () => {
    if (!selectedListId || loading || uniqueMatchData.length === 0) {
      return
    }
    setError(undefined)
    setLoading(true)
    try {
      // google place should not be null because it's already filtered, just adding the check to avoid typescript error
      const items = uniqueMatchData.map<UserListItem>((item) => ({
        type: 'place',
        data: {
          name: item.googlePlace?.name ?? '',
          address: item.googlePlace?.formatted_address ?? '',
          googlePlaceId: item.googlePlace?.place_id ?? '',
        },
      }))
      await userListsClient.addItemsToList(selectedListId, items)
      await setUpUserLists()
      setSuccesssMessage(`${uniqueMatchData.length} locations successfully added to your list`)
      setUploadSection(undefined)
      resetCsvFile()
      resetMatchedData()
    } catch {
      setError('Error adding items to list')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Container>
      <Filters filter={filter} onChageFilter={setFilter} />
      <GridTable headers={<Headers />} rows={rows} maxHeight={448} />
      {error && <Alert color="error" message={error} />}
      <Footer>
        <CancelButton color="primary" variant="outlined" onClick={onCancel}>
          Cancel
        </CancelButton>
        <ConfirmButton
          disabled={uniqueMatchData.length === 0}
          variant="contained"
          startIcon={loading ? <Spinner size={14} /> : undefined}
          onClick={onConfirm}>{`Insert ${matchedQ} locations into list`}</ConfirmButton>
      </Footer>
    </Container>
  )
}
